import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProducts, useSearchOff, useCreateProduct, useDeleteProduct, useUpdateProduct } from '@/hooks/useProducts';
import { useToast } from '@/components/ui/ToastProvider';
import { requireAuth } from '@/lib/auth/routeGuards';
import type { OffProduct, Product } from '@/types/api';

type ProductsSearch = { page?: number };

export const Route = createFileRoute('/products/')({
  beforeLoad: requireAuth,
  component: ProductsPage,
  validateSearch: (search: Record<string, unknown>): ProductsSearch => ({
    page: Number(search.page) || 1,
  }),
});

function NutritionBadge({ value, unit, label }: { value?: number; unit: string; label: string }) {
  if (value == null) return null;
  return (
    <span className="text-xs text-gray-500">
      {label} {Math.round(value * 10) / 10}{unit}
    </span>
  );
}

function ProductCard({ product, onDelete, onToggleFavorite }: {
  product: Product;
  onDelete: (id: number) => void;
  onToggleFavorite: (id: number, isFavorite: boolean) => void;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start gap-3">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="w-12 h-12 rounded object-cover flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{product.name}</h3>
            <button
              type="button"
              onClick={() => onToggleFavorite(product.id, !product.isFavorite)}
              className={`flex-shrink-0 ${product.isFavorite ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          </div>
          {product.brand && <p className="text-xs text-gray-500">{product.brand}</p>}
          <div className="flex flex-wrap gap-2 mt-1">
            <NutritionBadge value={product.energyKcal100g} unit=" kcal" label="" />
            <NutritionBadge value={product.proteins100g} unit="g" label="P" />
            <NutritionBadge value={product.carbohydrates100g} unit="g" label="G" />
            <NutritionBadge value={product.fat100g} unit="g" label="L" />
            <NutritionBadge value={product.fiber100g} unit="g" label="F" />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onDelete(product.id)}
          className="text-gray-400 hover:text-red-500 flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function OffProductRow({ product, onImport, isImporting }: {
  product: OffProduct;
  onImport: (p: OffProduct) => void;
  isImporting: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
      {product.imageUrl ? (
        <img src={product.imageUrl} alt={product.name} className="w-10 h-10 rounded object-cover flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded bg-gray-100 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
        {product.brand && <p className="text-xs text-gray-500">{product.brand}</p>}
        <div className="flex gap-2 mt-0.5">
          <NutritionBadge value={product.energyKcal100g} unit=" kcal" label="" />
          <NutritionBadge value={product.proteins100g} unit="g" label="P" />
          <NutritionBadge value={product.fat100g} unit="g" label="L" />
        </div>
      </div>
      <button
        type="button"
        onClick={() => onImport(product)}
        disabled={isImporting}
        className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
      >
        {t('products.import')}
      </button>
    </div>
  );
}

function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { page = 1 } = Route.useSearch();
  const limit = 20;

  const [searchQuery, setSearchQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [offQuery, setOffQuery] = useState('');
  const [offSearchTerm, setOffSearchTerm] = useState('');
  const [showOffSearch, setShowOffSearch] = useState(false);

  const setPage = (newPage: number) => {
    navigate({ to: '/products', search: { page: newPage } });
  };

  const { data, isLoading, error } = useProducts({
    query: searchQuery || undefined,
    favoritesOnly: favoritesOnly || undefined,
    page,
    limit,
  });

  const { data: offData, isLoading: offLoading } = useSearchOff(
    offSearchTerm ? { query: offSearchTerm } : null,
  );

  const createMutation = useCreateProduct();
  const deleteMutation = useDeleteProduct();
  const updateMutation = useUpdateProduct();

  const products = data?.products ?? [];
  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleOffSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setOffSearchTerm(offQuery);
  };

  const handleImport = async (offProduct: OffProduct) => {
    try {
      await createMutation.mutateAsync({
        barcode: offProduct.barcode,
        name: offProduct.name,
        brand: offProduct.brand,
        imageUrl: offProduct.imageUrl,
        energyKcal100g: offProduct.energyKcal100g,
        proteins100g: offProduct.proteins100g,
        carbohydrates100g: offProduct.carbohydrates100g,
        fat100g: offProduct.fat100g,
        fiber100g: offProduct.fiber100g,
        servingSize: offProduct.servingSize,
        source: 'openfoodfacts',
      });
      toast.success(t('products.imported'));
    } catch {
      toast.error(t('products.alreadyImported'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('products.deleteConfirm'))) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t('products.deleted'));
    } catch {
      toast.error(t('products.errorLoading'));
    }
  };

  const handleToggleFavorite = async (id: number, isFavorite: boolean) => {
    await updateMutation.mutateAsync({ id, data: { isFavorite } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('products.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">{t('products.description')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowOffSearch(!showOffSearch)}
            className="px-4 py-2 bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium rounded-md shadow-sm transition-colors"
          >
            {t('products.importFromOff')}
          </button>
        </div>

        {/* OpenFoodFacts search panel */}
        {showOffSearch && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 border border-primary-200">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('products.searchOff')}</h2>
            <form onSubmit={handleOffSearch} className="flex gap-2 mb-3">
              <input
                type="text"
                value={offQuery}
                onChange={(e) => setOffQuery(e.target.value)}
                placeholder={t('products.searchOffPlaceholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="submit"
                disabled={!offQuery.trim() || offLoading}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
              >
                {offLoading ? t('app.loading') : t('common.search', 'Rechercher')}
              </button>
            </form>

            {offData && offData.products.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">{t('products.noOffResults')}</p>
            )}

            {offData && offData.products.length > 0 && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {offData.products.map((p) => (
                  <OffProductRow
                    key={p.barcode}
                    product={p}
                    onImport={handleImport}
                    isImporting={createMutation.isPending}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search & filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex gap-3 items-center">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('products.search')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={favoritesOnly}
                onChange={(e) => { setFavoritesOnly(e.target.checked); setPage(1); }}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {t('products.favoritesOnly')}
            </label>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <p className="text-sm text-red-700">{t('products.errorLoading')}</p>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 h-20 animate-pulse" />
            ))}
          </div>
        )}

        {/* Product list */}
        {!isLoading && products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('products.noProducts')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('products.noProductsDescription')}</p>
          </div>
        )}

        {!isLoading && products.length > 0 && (
          <div className="space-y-3">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onDelete={handleDelete}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {page} sur {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previousPage')}
              </button>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.nextPage')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
